import { faSearch, faTimes } from '@fortawesome/pro-regular-svg-icons'
import { Flex, TextInput, type TextInputProps } from '@mantine/core'
import { useDebouncedState } from '@mantine/hooks'
import { useEffect, useRef } from 'react'

import { useTranslation } from '@flecto/translations'

import { Icon } from '../../src'

export type InputSearchProps = Omit<TextInputProps, 'onChange'> & {
  delay?: number
  clearable?: boolean
  iconSearchPosition?: 'left' | 'right'
  withIconSearch?: boolean
  onChange?: (term: string) => void
}

export function InputSearch({
  delay = 500,
  clearable = true,
  iconSearchPosition = 'left',
  withIconSearch = true,
  ...props
}: InputSearchProps) {
  const { t } = useTranslation('shared', { keyPrefix: 'actions' })
  const [value, setValue] = useDebouncedState('', delay)
  const ref = useRef<HTMLInputElement>(null)

  const handleClearSearch = () => {
    if (clearable && value) {
      setValue('')

      if (ref.current) {
        ref.current.value = ''
        ref.current.focus()
      }
    }
  }

  useEffect(() => {
    if (value !== props.value) {
      props.onChange?.(String(value))
    }
  }, [value])

  useEffect(() => {
    if (!props.value) {
      return
    }

    setValue(String(props.value ?? ''))
  }, [props.value])

  const iconSearch = <Icon icon={faSearch} />

  const leftSection =
    withIconSearch && iconSearchPosition === 'left' ? iconSearch : undefined

  return (
    <TextInput
      {...props}
      ref={ref}
      value={undefined}
      leftSection={leftSection}
      placeholder={props.placeholder ?? t('search')}
      defaultValue={value}
      onChange={(e) => setValue(e.currentTarget.value)}
      rightSection={
        <Flex align='center' gap='sm' justify='flex-end' w={10}>
          <Flex
            onClick={handleClearSearch}
            style={{ cursor: 'pointer' }}
            align='center'
          >
            {clearable && value ? <Icon icon={faTimes} color='gray.4' /> : null}
          </Flex>
          {iconSearchPosition === 'right' && withIconSearch ? iconSearch : null}
        </Flex>
      }
    />
  )
}
